import React from "react";
import { Box, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { FatigueDerivedMetric } from "../data/types";
import {
  AnimatedCounter,
  GlassCard,
  MetricRing,
  TrendChip,
  tierColor,
} from "./primitives";
import { RiskTierBadge } from "./RiskTierBadge";

interface KpiCardProps {
  metric: FatigueDerivedMetric;
  /** Plain-English explanation surfaced via the info tooltip. */
  explanation: string;
  onClick?: () => void;
  decimals?: number;
}

/** Headline KPI card: ring fill, animated value, trend vs baseline, RAG tier. */
export const KpiCard: React.FC<KpiCardProps> = ({
  metric,
  explanation,
  onClick,
  decimals = 0,
}) => {
  const theme = useTheme();
  const accent = tierColor(metric.riskTier, theme);
  const interactive = Boolean(onClick);

  return (
    <GlassCard accent={accent} glow={metric.riskTier === "red"}>
      <Box
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive ? `${metric.name} — open detail` : undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (interactive && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onClick?.();
          }
        }}
        sx={{
          cursor: interactive ? "pointer" : "default",
          outline: "none",
          "&:focus-visible": {
            boxShadow: `0 0 0 2px ${alpha(accent, 0.6)}`,
            borderRadius: 2,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Stack spacing={0.25}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
                {metric.code}
              </Typography>
              <Tooltip title={explanation} arrow>
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", fontSize: 16 }}
                  aria-label={`About ${metric.name}`}
                />
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {metric.name}
            </Typography>
          </Stack>
          <RiskTierBadge tier={metric.riskTier} size="small" />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <MetricRing
            value={metric.fill}
            display={`${Math.round(metric.value)}`}
            color={accent}
            size={104}
          />
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} lineHeight={1}>
              <AnimatedCounter value={metric.value} decimals={decimals} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {metric.unit}
            </Typography>
            <TrendChip
              trend={metric.trend}
              label={metric.trendLabel}
              goodDirection={metric.goodDirection}
            />
            <Typography variant="caption" color="text.secondary">
              {metric.baselineLabel}
            </Typography>
          </Stack>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}
        >
          {metric.question}
        </Typography>
      </Box>
    </GlassCard>
  );
};
